import React, { useEffect, useRef } from 'react';
import { Box, IconButton, Paper, Typography } from '@mui/material';
import i18n from '../../i18n';
import CommonInput from '../../component/form/common-input.component';
import { getMainGroupNameForDisplay } from '../../utils/menu-main-group-name-i18n';
import type { CreateMenuSubcategoryDraft, MenuLibraryManagementState } from './menu-library-management-state';
import { MenuLanguageMenuButton } from './menu-library-language-menu-button.component';
import { ReactComponent as BaselineDeleteForeverIcon } from '../../static/images/svg/ic_baseline-delete-forever.svg';
import { ReactComponent as TablerChevronDownIcon } from '../../static/images/svg/tabler_chevron-down.svg';

/** 子类别行可点击切换为输入框的字段（名称与两类数量限制） */
export type SubcategoryDraftFieldKey = 'name' | 'perPersonLimit' | 'perTableLimit';

/** 每人/桌台限制：仅数字，0–10000 */
const SUBCATEGORY_ORDER_LIMIT_MAX = 10000;

/**
 * 过滤非数字并限制在 0–10000（用于每人/桌台列输入）
 * @param raw 原始输入
 */
function clampSubcategoryOrderLimitDigits(raw: string): string {
    const digits = raw.replace(/\D/g, '');
    if (digits === '') {
        return '';
    }
    const n = parseInt(digits, 10);
    if (Number.isNaN(n)) {
        return '';
    }
    return String(Math.min(SUBCATEGORY_ORDER_LIMIT_MAX, Math.max(0, n)));
}

type SubcategoryDraftInlineFieldProps = {
    /** 菜单库状态 */
    state: MenuLibraryManagementState;
    /** 当前草稿行 */
    row: CreateMenuSubcategoryDraft;
    /** 编辑列 */
    field: SubcategoryDraftFieldKey;
    /** 输入框占位 */
    placeholder: string;
    /** 空值时展示的只读文案 */
    emptyDisplay: string;
    /** 是否处于输入态 */
    isEditing: boolean;
    /** 进入输入态 */
    onStartEdit: () => void;
    /** 结束输入态（失焦后） */
    onEndEdit: () => void;
};

/**
 * 子类别草稿列：默认展示文本，点击后切换为输入框；失焦由父级触发持久化占位请求
 */
function SubcategoryDraftInlineField(props: SubcategoryDraftInlineFieldProps) {
    const { state, row, field, placeholder, emptyDisplay, isEditing, onStartEdit, onEndEdit } = props;
    const value =
        field === 'name'
            ? getMainGroupNameForDisplay({
                primary: row.name,
                i18nJson: row.nameI18nJson,
                lang: row.displayLang,
            })
            : field === 'perPersonLimit'
                ? row.perPersonLimit
                : row.perTableLimit;

    const isOrderLimitField = field === 'perPersonLimit' || field === 'perTableLimit';

    /** 进入编辑态前的字段值，用于失焦时判断是否与 `persist` 请求前一致 */
    const valueBeforeEditRef = useRef('');
    /** 上一帧是否在编辑态，用于仅在「刚进入编辑」时写入快照 */
    const prevIsEditingRef = useRef(false);

    useEffect(() => {
        if (isEditing && !prevIsEditingRef.current) {
            valueBeforeEditRef.current =
                field === 'name'
                    ? getMainGroupNameForDisplay({
                        primary: row.name,
                        i18nJson: row.nameI18nJson,
                        lang: row.displayLang,
                    })
                    : field === 'perPersonLimit'
                        ? row.perPersonLimit
                        : row.perTableLimit;
        }
        prevIsEditingRef.current = isEditing;
    }, [isEditing, field, row.name, row.nameI18nJson, row.displayLang, row.perPersonLimit, row.perTableLimit]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let v = e.currentTarget.value;
        if (isOrderLimitField) {
            v = clampSubcategoryOrderLimitDigits(v);
        }
        if (field === 'name') {
            state.setCreateMenuSubcategoryDraftName(row.tempId, v);
        } else if (field === 'perPersonLimit') {
            state.setCreateMenuSubcategoryDraftPerPersonLimit(row.tempId, v);
        } else {
            state.setCreateMenuSubcategoryDraftPerTableLimit(row.tempId, v);
        }
    };

    const handleBlur = () => {
        let persistValue = value;
        if (isOrderLimitField) {
            persistValue = clampSubcategoryOrderLimitDigits(value);
            if (persistValue !== value) {
                if (field === 'perPersonLimit') {
                    state.setCreateMenuSubcategoryDraftPerPersonLimit(row.tempId, persistValue);
                } else {
                    state.setCreateMenuSubcategoryDraftPerTableLimit(row.tempId, persistValue);
                }
            }
        }
        const unchanged = state.isCreateMenuSubcategoryFieldUnchangedForPersist(
            field,
            persistValue,
            valueBeforeEditRef.current,
        );
        if (!unchanged) {
            void state.persistCreateMenuSubcategoryDraftField(row.tempId, field, persistValue);
        }
        onEndEdit();
    };

    if (isEditing) {
        return (
            <Box
                className="menu-library-create-dialog__sub-inline-slot menu-library-create-dialog__sub-inline-slot--editing"
            >
                <CommonInput
                    key={`${row.tempId}-${field}`}
                    inputType="INPUT"
                    variant="outlined"
                    fullWidth
                    size="small"
                    label=""
                    placeholder={placeholder}
                    value={value}
                    autoFocus
                    type={isOrderLimitField ? 'text' : undefined}
                    inputProps={
                        isOrderLimitField
                            ? {
                                inputMode: 'numeric' as const,
                                pattern: '[0-9]*',
                                'aria-valuemin': 0,
                                'aria-valuemax': SUBCATEGORY_ORDER_LIMIT_MAX,
                            }
                            : undefined
                    }
                    onChange={handleChange}
                    onBlur={handleBlur}
                />
            </Box>
        );
    }

    const displayText = value.trim().length > 0 ? value : emptyDisplay;

    return (
        <Box
            className="menu-library-create-dialog__sub-inline-slot menu-library-create-dialog__sub-inline-touch"
            role="button"
            tabIndex={0}
            onClick={(e) => {
                e.stopPropagation();
                onStartEdit();
            }}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onStartEdit();
                }
            }}
        >
            <Typography className="menu-library-create-dialog__sub-inline-value" noWrap>
                {displayText}
            </Typography>
        </Box>
    );
}

/**
 * 新建弹窗 — 子类别列表单列配置（与 `DISH_TABLE_HEAD` 顺序一致：语言 → 名称 → 每人限制 → 桌台限制）
 */
type SubcategoryDraftRowColumn =
    | { kind: 'lang'; labelKey: string }
    | {
        kind: 'inline';
        field: SubcategoryDraftFieldKey;
        labelKey: string;
        placeholderKey: string;
        /** 空值展示：`-` 或走 i18n「暂无」 */
        emptyDisplay: 'dash' | 'none_i18n';
        /** 名称列额外 class */
        colClassName?: string;
    };

const SUBCATEGORY_DRAFT_ROW_COLUMNS: SubcategoryDraftRowColumn[] = [
    { kind: 'lang', labelKey: 'menu_library_create_dialog_subcategory_col_lang' },
    {
        kind: 'inline',
        field: 'name',
        labelKey: 'menu_library_create_dialog_subcategory_col_name',
        placeholderKey: 'menu_library_create_dialog_subcategory_name_placeholder',
        emptyDisplay: 'dash',
        colClassName: 'menu-library-create-dialog__sub-col--name',
    },
    {
        kind: 'inline',
        field: 'perPersonLimit',
        labelKey: 'menu_library_create_dialog_subcategory_col_per_person',
        placeholderKey: 'menu_library_create_dialog_subcategory_limit_placeholder',
        emptyDisplay: 'none_i18n',
    },
    {
        kind: 'inline',
        field: 'perTableLimit',
        labelKey: 'menu_library_create_dialog_subcategory_col_per_table',
        placeholderKey: 'menu_library_create_dialog_subcategory_limit_placeholder',
        emptyDisplay: 'none_i18n',
    },
];

/** 列表行：可点进子分类详情 + 内联编辑 + 删除 */
export type CreateMenuSubcategoryDraftRowListProps = {
    /** 固定为列表模式（可省略，默认 `list`） */
    variant?: 'list';
    /** 菜单库状态 */
    state: MenuLibraryManagementState;
    /** 子类别草稿行 */
    row: CreateMenuSubcategoryDraft;
    /** 当前内联编辑列 */
    subcategoryEdit: { tempId: string; field: SubcategoryDraftFieldKey } | null;
    /** 设置内联编辑列 */
    setSubcategoryEdit: React.Dispatch<
        React.SetStateAction<{ tempId: string; field: SubcategoryDraftFieldKey } | null>
    >;
    /** 打开删除子类别确认 */
    setSubcategoryDeleteConfirmTempId: (tempId: string) => void;
};

/** 子分类详情顶栏摘要：与列表相同内联编辑；右侧箭头为向下；无删除 */
export type CreateMenuSubcategoryDraftRowDetailProps = {
    variant: 'detail';
    state: MenuLibraryManagementState;
    row: CreateMenuSubcategoryDraft;
    subcategoryEdit: { tempId: string; field: SubcategoryDraftFieldKey } | null;
    setSubcategoryEdit: React.Dispatch<
        React.SetStateAction<{ tempId: string; field: SubcategoryDraftFieldKey } | null>
    >;
};

export type CreateMenuSubcategoryDraftRowProps =
    | CreateMenuSubcategoryDraftRowListProps
    | CreateMenuSubcategoryDraftRowDetailProps;

/**
 * 新建菜单弹窗 — 单条子类别草稿行
 *
 * - **list**（默认）：整块可点击进入子分类详情；语言/名称/限制内联编辑；删除 + chevron（向右）
 * - **detail**：仅 `sub-card-inner`（由外层 `Paper` 包）；语言/名称/限制内联编辑同列表；chevron **向下**；无删除
 */
export function CreateMenuSubcategoryDraftRow(props: CreateMenuSubcategoryDraftRowProps) {
    const isDetail = props.variant === 'detail';
    const { state, row } = props;

    const cardInner = (
        <Box className="menu-library-create-dialog__sub-card-inner">
            {!isDetail && (
                <IconButton
                    type="button"
                    size="small"
                    aria-label={i18n.t('menu_library_create_dialog_remove_subcategory')}
                    className="menu-library-create-dialog__sub-delete"
                    onClick={(e) => {
                        e.stopPropagation();
                        if (props.subcategoryEdit?.tempId === row.tempId) {
                            props.setSubcategoryEdit(null);
                        }
                        props.setSubcategoryDeleteConfirmTempId(row.tempId);
                    }}
                >
                        <BaselineDeleteForeverIcon className="menu-library-create-dialog__sub-delete-icon" aria-hidden />
                </IconButton>
            )}
            {SUBCATEGORY_DRAFT_ROW_COLUMNS.map((col) => {
                const colClass =
                    col.kind === 'inline' && col.colClassName
                        ? `menu-library-create-dialog__sub-col ${col.colClassName}`
                        : 'menu-library-create-dialog__sub-col';
                return (
                    <Box
                        key={col.kind === 'lang' ? 'lang' : col.field}
                        className={colClass}
                        // onClick={(e) => e.stopPropagation()}
                    >
                        <Typography className="menu-library-create-dialog__sub-col-label">
                            {i18n.t(col.labelKey)}
                        </Typography>
                        {col.kind === 'lang' ? (
                            <>
                                {/*
                                 * 子类别名称：`row.displayLang` + `row.name`（主）+ `row.nameI18nJson`（与 RBP2 `dmi_slu_name` / `dmi_slu_name_i18n` 一致）。
                                 */}
                                <MenuLanguageMenuButton
                                    state={state}
                                    value={row.displayLang}
                                    onChange={(lang) => {
                                        if (props.subcategoryEdit?.tempId === row.tempId) {
                                            props.setSubcategoryEdit(null);
                                        }
                                        state.setCreateMenuSubcategoryDraftDisplayLang(row.tempId, lang);
                                    }}
                                    variant="subcategoryRow"
                                />
                            </>
                        ) : (
                            <SubcategoryDraftInlineField
                                key={
                                    col.field === 'name'
                                        ? `${row.tempId}-name-${row.displayLang}`
                                        : `${row.tempId}-${col.field}`
                                }
                                state={state}
                                row={row}
                                field={col.field}
                                placeholder={i18n.t(col.placeholderKey)}
                                emptyDisplay={
                                    col.emptyDisplay === 'dash'
                                        ? '-'
                                        : i18n.t('menu_library_create_dialog_placeholder_none')
                                }
                                isEditing={
                                    props.subcategoryEdit?.tempId === row.tempId &&
                                    props.subcategoryEdit?.field === col.field
                                }
                                onStartEdit={() =>
                                    props.setSubcategoryEdit({ tempId: row.tempId, field: col.field })
                                }
                                onEndEdit={() => props.setSubcategoryEdit(null)}
                            />
                        )}
                    </Box>
                );
            })}
            <Box
                className={
                    isDetail
                        ? 'menu-library-create-dialog__sub-card-actions menu-library-create-dialog__sub-card-actions--detail'
                        : 'menu-library-create-dialog__sub-card-actions'
                }
            >

                {isDetail ? (
                    <IconButton
                        type="button"
                        size="small"
                        className="menu-library-create-dialog__sub-chevron-btn"
                        aria-label={i18n.t('menu_library_create_dialog_back_from_subcategory_detail')}
                        onClick={(e) => {
                            e.stopPropagation();
                            state.closeCreateMenuSubcategoryDetail();
                        }}
                    >
                        <TablerChevronDownIcon
                            className="menu-library-create-dialog__sub-chevron menu-library-create-dialog__sub-chevron--detail-down"
                            aria-hidden
                        />
                    </IconButton>
                ) : (
                    <TablerChevronDownIcon className="menu-library-create-dialog__sub-chevron" aria-hidden />
                )}
            </Box>
        </Box>
    );

    if (isDetail) {
        return cardInner;
    }

    return (
        <Paper
            variant="outlined"
            elevation={0}
            className="menu-library-create-dialog__sub-card menu-library-create-dialog__sub-card--clickable"
            role="button"
            tabIndex={0}
            onClick={() => state.openCreateMenuSubcategoryDetail(row.tempId)}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    state.openCreateMenuSubcategoryDetail(row.tempId);
                }
            }}
        >
            {cardInner}
        </Paper>
    );
}
